import { Slot } from '@radix-ui/react-slot';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';
import { composeVariants, cva, type VariantProps } from '../../utils/cva';
import { useFocusVisible } from '../../utils/use-focus-visible';
import { buttonBaseVariants } from '../Button/ButtonBase';

const iconButtonVariants = composeVariants(
  buttonBaseVariants,
  cva({
    base: [
      'group',
      'flex',
      'items-center',
      'justify-center',
      '[--icon-size:1.4em]',
      'flex-none',
    ],
    variants: {
      size: {
        sm: ['text-xs', 'w-7', 'h-7'],
        md: ['text-sm', 'w-8', 'h-8'],
        lg: ['text-md', 'w-10', 'h-10'],
        xl: ['text-lg', 'w-14', 'h-14'],
      },
      shape: {
        square: ['rounded-md', 'before:rounded-md'],
        circle: ['rounded-full', 'before:rounded-full'],
      },
    },
  }),
);

export interface IconButtonProps
  extends ComponentPropsWithoutRef<'button'>,
    VariantProps<typeof iconButtonVariants> {
  asChild?: boolean;
  'data-testid'?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (props, ref) => {
    const {
      className,
      variant = 'primary',
      shape = 'square',
      size = 'md',
      inert,
      asChild = false,
      children,
      onBlur,
      onFocus,
      'data-testid': dataTestId = 'icon-button',
      ...rest
    } = props;

    const Component = asChild ? Slot : 'button';

    return (
      <Component
        ref={ref}
        type={asChild ? undefined : 'button'}
        data-testid={dataTestId}
        className={iconButtonVariants({
          size,
          shape,
          variant,
          inert,
          className,
        })}
        {...useFocusVisible({ onBlur, onFocus })}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);
